import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { LogIn } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { useAuthStore } from '../store/authStore';
import { registerThisDevice } from '../services/vaultDevices';
import './Vault.css';

const RECHECK_MS = 60 * 1000;

/** The vault's sync error says this computer was removed from the device list (card X1). */
export const isDeviceRevoked = (status) => String(status?.lastError || '').includes('device_revoked');

/**
 * This computer was removed from the vault's device list on another device
 * (card X1). The vault HERE is kept exactly as it is — a revoke only stops
 * the cloud copy reaching this computer — and sync waits. Signing in again
 * here (a new sign-in, so the person proves it is them) brings it back.
 */
export default function VaultDeviceRevokedBanner() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(async () => {
    try {
      setStatus(await invoke('vault_sync_status'));
    } catch {
      // Keep the last answer.
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    read();
    const timer = setInterval(read, RECHECK_MS);
    const subs = ['vault:changed'].map((e) => subscribeTauriEvent(e, read));
    return () => {
      clearInterval(timer);
      subs.forEach((s) => s?.unlisten?.());
    };
  }, [read]);

  const signIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await useAuthStore.getState().loginWithBrowser();
      if (result?.success) {
        await registerThisDevice({ force: true });
        if (status?.scopeId) await invoke('sync_pull_now', { workspaceId: status.scopeId }).catch(() => {});
      }
    } finally {
      setBusy(false);
      read();
    }
  };

  if (!isTauri() || !isDeviceRevoked(status)) return null;

  return (
    <div className="vault-pass-banner vault-pass-banner--warn" data-testid="vault-device-revoked">
      <LogIn size={15} className="vault-pass-banner-icon" />
      <div className="vault-pass-banner-texts">
        <span className="vault-pass-banner-title">
          {t('vault.deviceRevokedTitle', 'This computer was removed from your cloud vault')}
        </span>
        <span className="vault-pass-banner-text">
          {t(
            'vault.deviceRevokedBody',
            'Your vault on this computer is kept as it is; changes here wait. If that was you, sign in again to sync this computer. If not, change your master passphrase.'
          )}
        </span>
      </div>
      <div className="vault-pass-banner-actions">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors modern-button text-sm"
          onClick={signIn}
          disabled={busy}
        >
          {busy ? t('vault.rekeyPushReauthBusy', 'Signing in…') : t('vault.rekeyPushReauthAction', 'Sign in')}
        </button>
      </div>
    </div>
  );
}
